import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import { usePageContext, useRidesBulkActions } from "@ryde/redux";
import { bulkRecalculateSupplierPriceOptionsDrawerAF } from "@ryde/shared";

import {
  BulkRecalculateAdvancedOptionsCheckbox,
  BulkRecalculateOptionsContentStyled,
  BulkRecalculateRateType,
  BulkRecalculateSelectionRadioGroup,
  BulkRecalculateSwitchOption,
  useBulkRecalculateSelection,
} from "./parts";

export const BulkRecalculateSupplierPriceOptionsRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const setBulkRecalculateSupplierPriceOptionsDrawerOpened = useSetRecoilState(
    bulkRecalculateSupplierPriceOptionsDrawerAF(entity),
  );

  const [shouldIncludeManualPrices, setShouldIncludeManualPrices] =
    useState(false);
  const [recalculateDoneRides, setRecalculateDoneRides] = useState(false);
  const [isAdvancedSelectionEnabled, setIsAdvancedSelectionEnabled] =
    useState(false);
  const {
    selectionScope,
    onSelectionScopeChange,
    selectionCounts,
    switchCounts,
    selectedRideIdsForRecalculate,
  } = useBulkRecalculateSelection(
    BulkRecalculateRateType.SupplierPriceRate,
    isAdvancedSelectionEnabled,
  );
  const { bulkRecalculateSupplierPrice } = useRidesBulkActions();

  const onCloseClick = useEvent((): void => {
    setBulkRecalculateSupplierPriceOptionsDrawerOpened(false);
  });

  const recalculateCount = selectedRideIdsForRecalculate.length;

  const onNextClick = useEvent(async (): Promise<void> => {
    const result = await bulkRecalculateSupplierPrice({
      ids: selectedRideIdsForRecalculate,
      shouldIncludeManualPrices,
      recalculateDoneRides,
    });

    if (result?.success) {
      setBulkRecalculateSupplierPriceOptionsDrawerOpened(false);
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseClick}>
      <FormDrawer
        onCloseDrawer={onCloseClick}
        onCancelClick={onCloseClick}
        title={t("general.RECALCULATE_SUPPLIER_PRICE")}
        onSaveClick={onNextClick}
        footerSaveLabel={t("general.RECALCULATE_SUPPLIER_PRICE_BUTTON", {
          count: recalculateCount,
        })}
        isSaveDisabled={recalculateCount === 0}
      >
        <BulkRecalculateOptionsContentStyled>
          <BulkRecalculateSwitchOption
            checked={shouldIncludeManualPrices}
            onChange={setShouldIncludeManualPrices}
            label={t(
              "general.RECALCULATE_SUPPLIER_PRICE_INCLUDE_MANUAL_PRICES",
              { count: switchCounts.withManualPrice },
            )}
            helperText={t(
              "general.RECALCULATE_SUPPLIER_PRICE_INCLUDE_MANUAL_PRICES_HELPER",
            )}
          />
          <BulkRecalculateSwitchOption
            checked={recalculateDoneRides}
            onChange={setRecalculateDoneRides}
            label={t("general.RECALCULATE_SUPPLIER_PRICE_INCLUDE_DONE_RIDES", {
              count: switchCounts.withDoneStatus,
            })}
            helperText={t(
              "general.RECALCULATE_SUPPLIER_PRICE_INCLUDE_DONE_RIDES_HELPER",
            )}
          />
          <BulkRecalculateAdvancedOptionsCheckbox
            checked={isAdvancedSelectionEnabled}
            onChange={setIsAdvancedSelectionEnabled}
            label={t("general.RECALCULATE_ADVANCED_OPTIONS")}
          />
          {isAdvancedSelectionEnabled && (
            <BulkRecalculateSelectionRadioGroup
              rateType={BulkRecalculateRateType.SupplierPriceRate}
              selectionScope={selectionScope}
              onSelectionScopeChange={onSelectionScopeChange}
              selectionCounts={selectionCounts}
            />
          )}
        </BulkRecalculateOptionsContentStyled>
      </FormDrawer>
    </BaseDrawer>
  );
};
